import { assign, filter, isUndefined } from 'lodash'

import { eventEmitter } from '@/event-bus'
import ApplicationRomaniaService from '@/services/retailer/ApplicationRomaniaService.js'
import ApplicationService from '@/services/retailer/ApplicationService.js'
import FarmerService from '@/services/retailer/FarmerService'
import { errorMessage } from '@/utilities/helpers'

import { FARMER_ERROR_MESSAGES, APPLICATION_ERROR_MESSAGES } from '@/constants/errorMessages'

export const namespaced = true

const getDefaultState = () => {
  return {
    step: 'create',
    tckn: '',
    phone: '',
    farmer_payment_date: '',
    retailer_payment_release_date: '',
    farmer: null,
    application: null,
    latest_uploaded_documents: [],
    errors: [],
    base_application: null,
    unmerged_documents: [],
    multiple_upload_qr_code: null
  }
}

export const state = getDefaultState()

export const mutations = {
  CHANGE_STEP(state, step) {
    state.step = step
  },
  SET_VALUE(state, parameters) {
    state[parameters.type] = parameters.value
  },
  SET_FARMER(state, farmer) {
    state.farmer = farmer
  },
  SET_FINDEKS_PHONES(state, phones) {
    state.findeks_phones = phones
  },
  SET_APPLICATION(state, application) {
    state.application = application
  },
  ADD_ERROR(state, error) {
    state.errors.push(error)
  },
  SET_ERRORS(state, errors) {
    state.errors = errors
  },
  SET_LATEST_UPLOADED_DOCUMENTS(state, documents) {
    state.latest_uploaded_documents = documents
  },
  RESET_STATE(state) {
    assign(state, getDefaultState())
  },
  SET_BASE_APPLICATION(state, application) {
    state.base_application = application
  },
  SET_UNMERGED_DOCUMENTS(state, documents) {
    state.unmerged_documents = documents
  },
  SET_MULTIPLE_UPLOAD_QR_CODE(state, qrCode) {
    state.multiple_upload_qr_code = qrCode
  },
  VERIFY_DIGITAL_SIGNATURE(state, document) {
    let index = state.unmerged_documents.findIndex(item => item.id === document.id)

    if (index === -1) {
      state.unmerged_documents.push(document)
    } else {
      state.unmerged_documents[index] = document
    }

    state.application.digital_signable_documents.map(item => {
      if (item.signable.id === document.id) {
        item.signable.is_digitally_signed = true

        return item
      }
    })
  }
}

export const getters = {
  step: state => {
    return state.step
  }
}

export const actions = {
  resetState({ commit }) {
    commit('RESET_STATE')
  },
  addError({ commit, state }, error) {
    const errorExists = filter(state.errors, item => {
      return item.type === error.type
    })

    if (errorExists.length === 0) {
      commit('ADD_ERROR', error)
    }
  },
  removeError({ commit, state }, errorType) {
    commit(
      'SET_ERRORS',
      filter(state.errors, error => {
        return error.type !== errorType
      })
    )
  },
  changeStep({ commit }, step) {
    commit('CHANGE_STEP', step)
  },
  setValue({ commit }, parameters) {
    commit('SET_VALUE', parameters)
  },
  getFarmerByTckn({ commit }, tckn) {
    return new Promise((resolve, reject) => {
      FarmerService.getFarmerByTckn(tckn)
        .then(response => {
          eventEmitter.emit('removeError', 'farmerByTckn')
          commit('SET_FARMER', response.data)
          resolve(response.data)
        })
        .catch(err => {
          eventEmitter.emit('addError', {
            type: 'farmerByTckn',
            message: errorMessage(err)
          })
          reject(err)
        })
    })
  },
  createApplication({ dispatch, commit }, params) {
    return new Promise((resolve, reject) => {
      ApplicationRomaniaService.createApplication({ payload: params })
        .then(response => {
          commit('SET_APPLICATION', response.data)
          resolve(response.data)
        })
        .catch(err => {
          if (isUndefined(err.response?.data?.type)) {
            const notification = {
              type: 'danger',
              message: errorMessage(err)
            }
            dispatch('notification/add', notification, {
              root: true
            })
          }

          reject(err)
        })
    })
  },
  completeApplication({ dispatch, state }) {
    return new Promise((resolve, reject) => {
      ApplicationRomaniaService.completeApplication(state.application.id)
        .then(response => {
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: APPLICATION_ERROR_MESSAGES.completeError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getApplication({ commit, dispatch, state }, applicationId) {
    if (state.application && state.application.id === applicationId) {
      return state.application
    }

    return new Promise((resolve, reject) => {
      ApplicationService.getApplication(applicationId)
        .then(response => {
          commit('SET_APPLICATION', response.data)
          commit('SET_FARMER', response.data.farmer)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: APPLICATION_ERROR_MESSAGES.getApplicationError
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  refreshApplication({ commit, dispatch }, applicationId) {
    return new Promise((resolve, reject) => {
      ApplicationService.getApplication(applicationId)
        .then(response => {
          commit('SET_APPLICATION', response.data)
          commit('SET_FARMER', response.data.farmer)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: APPLICATION_ERROR_MESSAGES.getApplicationError
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getBaseApplication({ commit }, params) {
    return new Promise((resolve, reject) => {
      ApplicationService.getBaseApplication(params.applicationId, params.salesChannelId)
        .then(response => {
          commit('SET_BASE_APPLICATION', response.data)
          resolve(response.data)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  getFarmer({ commit, dispatch, state }, farmerId) {
    if (state.farmer && farmerId === state.farmer.id) return state.farmer

    return new Promise((resolve, reject) => {
      FarmerService.getFarmer(farmerId)
        .then(response => {
          commit('SET_FARMER', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: FARMER_ERROR_MESSAGES.getFarmerError
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  saveFarmerInfoSurveyAnswers({ dispatch, state }, params) {
    return new Promise((resolve, reject) => {
      ApplicationRomaniaService.saveFarmerInfoSurveyAnswers(state.application.id, { payload: params })
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notificationCode = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notificationCode, {
            root: true
          })
          reject(err)
        })
    })
  },
  saveSurveyAnswers({ dispatch, state }, params) {
    return new Promise((resolve, reject) => {
      ApplicationRomaniaService.saveSurveyAnswers(state.application.id, { payload: params })
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notificationCode = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notificationCode, {
            root: true
          })
          reject(err)
        })
    })
  },
  saveDocuments({ dispatch, state }, params) {
    return new Promise((resolve, reject) => {
      ApplicationRomaniaService.saveDocuments(state.application.id, { payload: params })
        .then(response => {
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  uploadDocument({ dispatch, state }, file) {
    return new Promise((resolve, reject) => {
      ApplicationRomaniaService.uploadDocument(state.application.id, file)
        .then(response => {
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  uploadMultipleDocument({ dispatch }, page) {
    return new Promise((resolve, reject) => {
      ApplicationRomaniaService.uploadMultipleDocument(state.application.id, page)
        .then(response => {
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })

          reject(err)
        })
    })
  },
  deletePage({ dispatch }, { id, document, page }) {
    return new Promise((resolve, reject) => {
      ApplicationRomaniaService.deleteDocumentPage(id, document, page)
        .then(response => {
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })

          reject(err)
        })
    })
  },
  viewDocument({ dispatch }, { id, document }) {
    return new Promise((resolve, reject) => {
      ApplicationRomaniaService.viewDocument(id, document)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  viewDocumentPage({ dispatch }, { id, document, page }) {
    return new Promise((resolve, reject) => {
      ApplicationRomaniaService.viewDocumentPage(id, document, page)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getLatestUploadedDocuments({ commit, dispatch, state }) {
    return new Promise((resolve, reject) => {
      ApplicationRomaniaService.getLatestUploadedDocuments(state.application.id)
        .then(response => {
          commit('SET_LATEST_UPLOADED_DOCUMENTS', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getUnMergedDocuments({ commit, dispatch, state }) {
    return new Promise((resolve, reject) => {
      ApplicationRomaniaService.getUnMergedDocuments(state.application.id)
        .then(response => {
          commit('SET_UNMERGED_DOCUMENTS', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getMultipleFileUploadQrCode({ commit, dispatch, state }) {
    return new Promise((resolve, reject) => {
      ApplicationRomaniaService.getQrCode(state.application.id)
        .then(response => {
          commit('SET_MULTIPLE_UPLOAD_QR_CODE', response.qr_code)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  }
}
